import { CartSessionKeyProvider, useRegionCurrency } from '@skin-seoul/service';
import {
  CartDrawerContextProvider,
  useCartDrawer,
  ShoppingBagDrawer as ShoppingBagDrawerComponent,
  ShoppingBagButton as ShoppingBagButtonComponent,
} from '@skin-seoul/shared-components';
import type { ComponentProps } from 'react';
import { useEffect, useRef } from 'react';
import { BridgeApi, wpBridge } from '@skin-seoul/utils';

type ShoppingBagButtonProps = Pick<ComponentProps<typeof ShoppingBagButtonComponent>, 'iconProps'>;

const ShoppingBagButton = ({ iconProps }: ShoppingBagButtonProps) => {
  const { onCartToggle } = useCartDrawer();
  const { country } = useRegionCurrency();

  return <ShoppingBagButtonComponent country={country} onClick={() => onCartToggle()} iconProps={iconProps} />;
};

const ShoppingBagDrawer = () => {
  const { currency } = useRegionCurrency();
  const { onCartClose } = useCartDrawer();
  const push = (path: string) => {
    window.location.href = path;
  };

  const handleContinueShopping = () => {
    onCartClose();
    push('/');
  };

  return <ShoppingBagDrawerComponent currency={currency} onClickContinueShopping={handleContinueShopping} />;
};

type ShoppingBagDrawerButtonProps = ShoppingBagButtonProps & {
  listenToWPEvents?: boolean;
};

const CartDrawerWPListener = () => {
  const { onCartOpen, isOpen } = useCartDrawer();
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const off = wpBridge.on('add-to-cart', () => {
      const currentIsOpen = isOpenRef.current;

      // Don't reopen if cart is already open (user is interacting with it)
      if (currentIsOpen) {
        return;
      }

      if (BridgeApi.isWebview()) {
        BridgeApi.cartOpen();
        return;
      }

      onCartOpen();
    });

    return off;
  }, [onCartOpen]);

  return null;
};

const CartDrawerButton = ({ iconProps, listenToWPEvents = false }: ShoppingBagDrawerButtonProps) => {
  return (
    <CartDrawerContextProvider>
      <CartSessionKeyProvider>
        {listenToWPEvents && <CartDrawerWPListener />}
        <ShoppingBagButton iconProps={iconProps} />
        <ShoppingBagDrawer />
      </CartSessionKeyProvider>
    </CartDrawerContextProvider>
  );
};

export const HeaderCartButton = () => {
  return <CartDrawerButton iconProps={{ color: 'generic.0' }} listenToWPEvents={true} />;
};

export const MobileNavCartButton = () => {
  return <CartDrawerButton iconProps={{ color: 'neutral.200', strokeWidth: 0.5 }} listenToWPEvents={false} />;
};
